import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import adminApi from '../api/client';

const PAGE_SIZE = 20;

type DisputeAction = 'dismiss_dispute' | 'confirm_return';
type DisputePriorStatus = 'HELD' | 'RETURN_REQUESTED';

interface ExchangeItemDisputeItem {
  id: string;
  exchange_id: string;
  library_id: string;
  book_id: string | null;
  book_title: string | null;
  book_author: string | null;
  original_owner_id: string;
  original_owner_name: string | null;
  current_holder_id: string | null;
  current_holder_name: string | null;
  status: 'DISPUTED';
  overdue_since: string | null;
  is_overdue: boolean;
  dispute_prior_status: DisputePriorStatus | null;
}

interface ExchangeItemDispute {
  report_id: string;
  exchange_id: string;
  item: ExchangeItemDisputeItem;
  expected_return_date: string | null;
  reason: string;
  status: 'pending';
  created_at: string;
}

interface ExchangeItemDisputeQueue {
  data: ExchangeItemDispute[];
  page: number;
  limit: number;
  total: number;
}

interface ApiErrorShape {
  response?: {
    status?: number;
  };
}

class MalformedExchangeDisputeResponseError extends Error {
  constructor() {
    super('Malformed Exchange dispute response');
    this.name = 'MalformedExchangeDisputeResponseError';
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function requiredString(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) {
    throw new MalformedExchangeDisputeResponseError();
  }
  return value;
}

function nullableString(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string') throw new MalformedExchangeDisputeResponseError();
  return value;
}

function timestamp(value: unknown): string {
  const parsed = requiredString(value);
  if (!Number.isFinite(Date.parse(parsed))) {
    throw new MalformedExchangeDisputeResponseError();
  }
  return parsed;
}

function nullableTimestamp(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  return timestamp(value);
}

function nullableDate(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const parsed = requiredString(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(parsed) || !Number.isFinite(Date.parse(`${parsed}T00:00:00Z`))) {
    throw new MalformedExchangeDisputeResponseError();
  }
  return parsed;
}

function oneOf<T extends string>(value: unknown, values: readonly T[]): T {
  if (typeof value !== 'string' || !values.includes(value as T)) {
    throw new MalformedExchangeDisputeResponseError();
  }
  return value as T;
}

function integer(value: unknown, minimum: number, maximum?: number): number {
  if (!Number.isInteger(value) || Number(value) < minimum || (maximum !== undefined && Number(value) > maximum)) {
    throw new MalformedExchangeDisputeResponseError();
  }
  return Number(value);
}

function parseItem(value: unknown): ExchangeItemDisputeItem {
  if (!isRecord(value)) throw new MalformedExchangeDisputeResponseError();
  return {
    id: requiredString(value.id),
    exchange_id: requiredString(value.exchange_id),
    library_id: requiredString(value.library_id),
    book_id: nullableString(value.book_id),
    book_title: nullableString(value.book_title),
    book_author: nullableString(value.book_author),
    original_owner_id: requiredString(value.original_owner_id),
    original_owner_name: nullableString(value.original_owner_name),
    current_holder_id: nullableString(value.current_holder_id),
    current_holder_name: nullableString(value.current_holder_name),
    status: oneOf(value.status, ['DISPUTED'] as const),
    overdue_since: nullableTimestamp(value.overdue_since),
    is_overdue: typeof value.is_overdue === 'boolean'
      ? value.is_overdue
      : value.overdue_since !== null && value.overdue_since !== undefined,
    dispute_prior_status: value.dispute_prior_status === null || value.dispute_prior_status === undefined
      ? null
      : oneOf<DisputePriorStatus>(value.dispute_prior_status, ['HELD', 'RETURN_REQUESTED']),
  };
}

function parseDispute(value: unknown): ExchangeItemDispute {
  if (!isRecord(value)) throw new MalformedExchangeDisputeResponseError();
  return {
    report_id: requiredString(value.report_id),
    exchange_id: requiredString(value.exchange_id),
    item: parseItem(value.item),
    expected_return_date: nullableDate(value.expected_return_date),
    reason: requiredString(value.reason),
    status: oneOf(value.status, ['pending'] as const),
    created_at: timestamp(value.created_at),
  };
}

export function parseExchangeItemDisputeQueue(value: unknown): ExchangeItemDisputeQueue {
  if (!isRecord(value) || !Array.isArray(value.data)) {
    throw new MalformedExchangeDisputeResponseError();
  }
  return {
    data: value.data.map(parseDispute),
    page: integer(value.page, 1),
    limit: integer(value.limit, 1, 100),
    total: integer(value.total, 0),
  };
}

function statusOf(error: unknown): number | undefined {
  return (error as ApiErrorShape).response?.status;
}

function safeQueueErrorMessage(error: unknown): string {
  if (error instanceof MalformedExchangeDisputeResponseError) {
    return 'The server returned an invalid Exchange dispute response. Retry or contact an administrator.';
  }
  const status = statusOf(error);
  if (status === 401) return 'Your admin session expired. Please sign in again.';
  if (status === 403) return 'This admin session is not permitted to use Exchange dispute recovery.';
  if (status === 404) return 'The Exchange dispute queue is unavailable.';
  return 'The Exchange dispute service could not be reached. Retry if appropriate.';
}

function safeActionErrorMessage(error: unknown): string {
  const status = statusOf(error);
  if (status === 401) return 'Your admin session expired. Please sign in again.';
  if (status === 403) return 'This admin session is not permitted to resolve Exchange disputes.';
  if (status === 409) return 'This dispute changed before it could be resolved. Refresh the queue and review it again.';
  if (status === 422) return 'Enter a nonblank resolution note before confirming this action.';
  return 'The Exchange dispute could not be resolved. Retry if appropriate.';
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function formatDateOnly(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isFinite(parsed.getTime()) ? parsed.toLocaleDateString() : value;
}

function actionLabel(action: DisputeAction): string {
  return action === 'dismiss_dispute' ? 'Dismiss dispute' : 'Confirm verified return';
}

const ExchangeDisputes: React.FC = () => {
  const [page, setPage] = useState(1);
  const [queue, setQueue] = useState<ExchangeItemDisputeQueue | null>(null);
  const [queueLoading, setQueueLoading] = useState(true);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selected, setSelected] = useState<ExchangeItemDispute | null>(null);
  const [action, setAction] = useState<DisputeAction | null>(null);
  const [resolutionNote, setResolutionNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const queueRequest = useRef(0);

  const loadQueue = useCallback(async (requestedPage: number): Promise<boolean> => {
    const requestId = ++queueRequest.current;
    setQueueLoading(true);
    setQueueError(null);
    try {
      const response = await adminApi.get('/admin/exchange-items/disputes', {
        params: { page: requestedPage, limit: PAGE_SIZE },
      });
      const parsed = parseExchangeItemDisputeQueue(response.data);
      if (requestId !== queueRequest.current) return false;
      setQueue(parsed);
      return true;
    } catch (error) {
      if (requestId !== queueRequest.current) return false;
      setQueue(null);
      setQueueError(safeQueueErrorMessage(error));
      return false;
    } finally {
      if (requestId === queueRequest.current) setQueueLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadQueue(page);
    return () => {
      queueRequest.current += 1;
    };
  }, [loadQueue, page]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((queue?.total || 0) / (queue?.limit || PAGE_SIZE))),
    [queue],
  );

  const openAction = (dispute: ExchangeItemDispute, nextAction: DisputeAction) => {
    setSelected(dispute);
    setAction(nextAction);
    setResolutionNote('');
    setConfirmed(false);
    setActionError(null);
    setSuccessMessage(null);
  };

  const closeAction = (force = false) => {
    if (actionSubmitting && !force) return;
    setSelected(null);
    setAction(null);
    setResolutionNote('');
    setConfirmed(false);
    setActionError(null);
  };

  const submitAction = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected || !action || actionSubmitting) return;
    const note = resolutionNote.trim();
    if (!note) {
      setActionError('Enter a nonblank resolution note before confirming.');
      return;
    }
    if (!confirmed) {
      setActionError('Confirm that this action applies to the currently loaded dispute.');
      return;
    }

    setActionSubmitting(true);
    setActionError(null);
    setSuccessMessage(null);
    try {
      await adminApi.post(
        `/admin/exchange-items/${encodeURIComponent(selected.item.id)}/resolve`,
        { action, resolution_note: note },
      );
      closeAction(true);
      const refreshed = await loadQueue(page);
      setSuccessMessage(
        refreshed
          ? `${actionLabel(action)} completed and the queue was refreshed.`
          : `${actionLabel(action)} completed, but the queue refresh failed. Use Refresh queue to reconcile it.`,
      );
    } catch (error) {
      setActionError(safeActionErrorMessage(error));
    } finally {
      setActionSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F1EFE8] p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A6A38]">Exchange recovery</p>
            <h1 className="mt-2 text-3xl font-bold text-[#085041]">Pending Exchange disputes</h1>
            <p className="mt-2 max-w-3xl text-sm text-gray-600">
              Review only pending reports for currently disputed Exchange items. Resolution is limited to dismissing a
              dispute or confirming a verified return, with a required note and audit trail.
            </p>
          </div>
          <button
            type="button"
            onClick={() => { setSuccessMessage(null); void loadQueue(page); }}
            disabled={queueLoading}
            className="rounded-lg border border-[#085041] bg-white px-4 py-2 text-sm font-semibold text-[#085041] hover:bg-[#085041]/5 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {queueLoading ? 'Loading…' : 'Refresh queue'}
          </button>
        </header>

        {successMessage && <div role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{successMessage}</div>}
        {queueError && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <p>{queueError}</p>
            <button type="button" onClick={() => void loadQueue(page)} className="mt-3 rounded-lg border border-red-300 bg-white px-3 py-2 font-semibold text-red-800 hover:bg-red-50">Retry</button>
          </div>
        )}

        <section aria-labelledby="exchange-dispute-queue" className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 id="exchange-dispute-queue" className="text-lg font-semibold text-gray-800">Dispute queue</h2>
              <p className="mt-1 text-sm text-gray-500">Only <code>exchange_item</code> reports in <code>pending</code> status are eligible.</p>
            </div>
            {!queueLoading && queue && <span className="rounded-full bg-amber-50 px-3 py-1 text-sm font-semibold text-amber-800">{queue.total} pending</span>}
          </div>

          {queueLoading ? (
            <div role="status" className="py-16 text-center text-gray-500">Loading pending Exchange disputes…</div>
          ) : queueError ? null : !queue || queue.data.length === 0 ? (
            <div className="flex flex-col items-center py-16 text-center text-gray-500">
              <span className="mb-4 text-4xl">✅</span>
              <p className="text-lg font-medium">No pending Exchange disputes</p>
              <p className="mt-1 text-sm">The queue is clear.</p>
            </div>
          ) : (
            <div className="mt-5 grid gap-5">
              {queue.data.map((dispute) => (
                <article key={dispute.report_id} className="rounded-xl border border-gray-200 bg-gray-50 p-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-amber-800">DISPUTED</span>
                        {dispute.item.is_overdue && <span className="rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-red-800">Overdue</span>}
                      </div>
                      <h3 className="mt-3 text-lg font-semibold text-[#085041]">Report {dispute.report_id}</h3>
                      <p className="mt-1 text-sm text-gray-700"><span className="font-semibold">Reason:</span> {dispute.reason}</p>
                      <p className="mt-1 text-sm text-gray-500"><span className="font-semibold">Reported:</span> {formatDate(dispute.created_at)}</p>
                    </div>
                    <div className="flex flex-wrap gap-2 lg:justify-end">
                      <button type="button" onClick={() => openAction(dispute, 'dismiss_dispute')} className="rounded-lg border border-gray-400 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100">Dismiss dispute</button>
                      <button type="button" onClick={() => openAction(dispute, 'confirm_return')} className="rounded-lg bg-[#085041] px-3 py-2 text-sm font-semibold text-white hover:bg-[#063d32]">Confirm verified return</button>
                    </div>
                  </div>

                  <dl className="mt-5 grid gap-3 border-t border-gray-200 pt-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
                    <div><dt className="font-semibold text-gray-500">Exchange ID</dt><dd className="mt-1 break-all text-gray-800">{dispute.exchange_id}</dd></div>
                    <div><dt className="font-semibold text-gray-500">Exchange item ID</dt><dd className="mt-1 break-all text-gray-800">{dispute.item.id}</dd></div>
                    <div><dt className="font-semibold text-gray-500">Library copy ID</dt><dd className="mt-1 break-all text-gray-800">{dispute.item.library_id}</dd></div>
                    <div><dt className="font-semibold text-gray-500">Original owner</dt><dd className="mt-1 text-gray-800">{dispute.item.original_owner_name || 'Unavailable'}<span className="block break-all text-xs text-gray-500">ID: {dispute.item.original_owner_id}</span></dd></div>
                    <div><dt className="font-semibold text-gray-500">Current holder</dt><dd className="mt-1 text-gray-800">{dispute.item.current_holder_name || 'Unavailable'}<span className="block break-all text-xs text-gray-500">ID: {dispute.item.current_holder_id || '—'}</span></dd></div>
                    <div><dt className="font-semibold text-gray-500">Dispute prior status</dt><dd className="mt-1 text-gray-800">{dispute.item.dispute_prior_status || 'Not recorded'}</dd></div>
                    <div><dt className="font-semibold text-gray-500">Expected return date</dt><dd className="mt-1 text-gray-800">{dispute.expected_return_date ? formatDateOnly(dispute.expected_return_date) : 'Unavailable'}</dd></div>
                    <div><dt className="font-semibold text-gray-500">Overdue marker</dt><dd className="mt-1 text-gray-800">{dispute.item.is_overdue ? `Overdue since ${formatDate(dispute.item.overdue_since)}` : 'Not marked overdue'}</dd></div>
                    <div><dt className="font-semibold text-gray-500">Book identity</dt><dd className="mt-1 text-gray-800">{dispute.item.book_title || 'Unavailable'}<span className="block text-xs text-gray-500">Book ID: {dispute.item.book_id || '—'}{dispute.item.book_author ? ` · ${dispute.item.book_author}` : ''}</span></dd></div>
                  </dl>
                </article>
              ))}
            </div>
          )}
        </section>

        {!queueLoading && !queueError && queue && totalPages > 1 && (
          <nav aria-label="Exchange dispute pagination" className="flex items-center justify-center gap-2">
            <button type="button" disabled={page === 1} onClick={() => setPage(page - 1)} className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-[#085041] disabled:cursor-not-allowed disabled:opacity-50">Previous</button>
            <span className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700">Page {page} of {totalPages}</span>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-[#085041] disabled:cursor-not-allowed disabled:opacity-50">Next</button>
          </nav>
        )}
      </div>

      {selected && action && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <form role="dialog" aria-modal="true" aria-labelledby="exchange-dispute-action-title" onSubmit={submitAction} className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <h2 id="exchange-dispute-action-title" className="text-xl font-bold text-[#085041]">{actionLabel(action)}</h2>
            <p className="mt-2 text-sm text-gray-600">This will resolve report <span className="font-semibold">{selected.report_id}</span> for Exchange item <span className="font-semibold">{selected.item.id}</span>. The backend will re-check the current locked state.</p>

            {actionError && <div role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{actionError}</div>}
            <label htmlFor="exchange-resolution-note" className="mt-4 block text-sm font-semibold text-gray-800">Resolution note or evidence <span className="text-red-700">*</span></label>
            <textarea
              id="exchange-resolution-note"
              aria-label="Resolution note or evidence"
              value={resolutionNote}
              onChange={(event) => setResolutionNote(event.target.value)}
              disabled={actionSubmitting}
              className="mt-2 min-h-28 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[#085041] focus:outline-none focus:ring-2 focus:ring-[#085041]/20 disabled:bg-gray-100"
            />
            <label className="mt-3 flex items-start gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} disabled={actionSubmitting} className="mt-0.5" />
              <span>I confirm this {actionLabel(action).toLowerCase()} action applies to the currently loaded dispute.</span>
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => closeAction()} disabled={actionSubmitting} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 disabled:opacity-50">Cancel</button>
              <button type="submit" disabled={actionSubmitting} className="rounded-lg bg-[#085041] px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">{actionSubmitting ? 'Submitting…' : `Confirm ${actionLabel(action)}`}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ExchangeDisputes;
